import type { SystemHealth } from '@ww/shared';
import { I, Icon } from '../lib/icons';
import { OVERALL_LABELS, componentColor, modeStyle } from '../lib/status';
import { fmtDate, fmtInterval, fmtTime, tzAbbr, useNow } from '../lib/time';

export const DISCLAIMER =
  'Situational awareness and supplemental notification only. Not a replacement for official NWS warnings or emergency alert channels.';

export function Logo({ large = false }: { large?: boolean }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
      <Icon d={I.logo} size={large ? 34 : 30} strokeWidth={2} style={{ color: '#5aa9ff' }} />
      {/* Dropped below 900px: the icon alone still identifies the app, and the two
          lines of wordmark are ~150px the header cannot spare on a phone. */}
      <div className="logo-text" style={{ display: 'flex', flexDirection: 'column', lineHeight: 1 }}>
        <span className="cond" style={{ fontWeight: 700, fontSize: large ? 28 : 25, letterSpacing: '.01em' }}>
          WeatherWatch
        </span>
        <span
          style={{
            fontSize: 10.5,
            color: '#93a5c1',
            marginTop: 3,
            letterSpacing: '.1em',
            textTransform: 'uppercase',
          }}
        >
          Severe weather awareness
        </span>
      </div>
    </div>
  );
}

export function LiveClock({ tz, large = false }: { tz?: string | null; large?: boolean }) {
  const now = useNow(1000);
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'baseline',
        gap: 10,
        padding: '0 4px 0 14px',
        borderLeft: '1px solid #1f2d47',
        flexShrink: 0,
      }}
    >
      <span className="hide-md" style={{ fontSize: large ? 16 : 14, color: '#9fb0c8' }}>
        {fmtDate(now, tz)}
      </span>
      <span
        className="mono"
        style={{ fontSize: large ? 30 : 24, fontWeight: 500, letterSpacing: '-.02em' }}
        aria-live="off"
      >
        {fmtTime(now, tz, true)}
      </span>
      <span style={{ fontSize: large ? 15 : 13, color: '#9fb0c8' }}>{tzAbbr(tz, new Date(now))}</span>
    </div>
  );
}

export function PollingPill({ health, large = false }: { health: SystemHealth | null; large?: boolean }) {
  const p = health?.polling ?? null;
  const ms = modeStyle(p?.mode);
  const text = p ? `${ms.label} · ${fmtInterval(p.intervalMs).toUpperCase()}` : ms.label;
  return (
    <div
      className={ms.pulse ? 'cond live' : 'cond'}
      title={p?.modeReason || undefined}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        height: large ? 34 : 30,
        padding: '0 12px',
        borderRadius: 17,
        fontSize: large ? 16 : 14,
        fontWeight: 600,
        letterSpacing: '.08em',
        color: ms.color,
        background: ms.bg,
        border: `1px solid ${ms.border}`,
        whiteSpace: 'nowrap',
        flexShrink: 0,
      }}
    >
      <span className="live" style={{ width: 8, height: 8, borderRadius: '50%', background: 'currentColor' }} />
      <span>{text}</span>
    </div>
  );
}

export function SystemBadge({
  health,
  connected,
  large = false,
}: {
  health: SystemHealth | null;
  connected: boolean;
  large?: boolean;
}) {
  const overall = health?.overall ?? 'unknown';
  const stale = !!health?.stale;
  const color = stale ? '#ffb547' : componentColor(overall);
  const label = stale ? 'NWS feed stale' : OVERALL_LABELS[overall] ?? 'Status unknown';
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 14, fontSize: large ? 16 : 14, flexShrink: 0 }}>
      <span style={{ display: 'flex', alignItems: 'center', gap: 8, color }}>
        <span style={{ width: 9, height: 9, borderRadius: '50%', background: color }} />
        <span>{label}</span>
      </span>
      <span
        className="hide-md"
        title={connected ? 'Live updates connected' : 'Live updates reconnecting; refreshing every 60 s'}
        style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12.5, color: connected ? '#6fdc98' : '#9fb0c8' }}
      >
        <span
          className={connected ? 'live' : undefined}
          style={{ width: 7, height: 7, borderRadius: '50%', background: connected ? '#3fb950' : '#6b7a92' }}
        />
        {connected ? 'Live' : 'Reconnecting'}
      </span>
    </div>
  );
}
